import React, { FC, FormEvent, useEffect, useState } from "react";
import { set, getMonth, getYear, getDate } from "date-fns/esm";

import { LocationDto } from "@apiModels/LocationDto.csharp";
import { SpecializationDto } from "@apiModels/SpecializationDto.csharp";

import { Textarea } from "../../Partials/Textarea";
import { Input } from "../../Partials/Input";
import { FileInput } from "../../Partials/FileInput";

import { UrlHandler, UrlQueryEntryName } from "../../Utils/UrlHandler";
import { useInsuranceLabels } from "../../Utils/Context/labelsContext";
import { useInsuranceFilters } from "../../Utils/Context/filtersContext";

import { InsuranceFormFields, useInsuranceForm } from "./Utils/insuranceForm";
import { insuranceFormSubmit } from "./Utils/insuranceFormSubmit";
import { InsuranceFormSubmissionStatus } from "../../Utils/types";

import { BookingCalendar } from "./Components/Calendar/BookingCalendar";
import { BookingConfirmationModal } from "./Components/Modal/BookingConfirmationModal";

import { LocationsCombobox } from "../LocationsCombobox";
import { SpecializationsCombobox } from "../SpecializationsCombobox";
import { ServicesCombobox } from "../ServicesCombobox";
import { TimeSelect } from "../TimeSelect";
import { FormControls } from "../FormControls";
import { StreamLineIcon } from "../../../SvgIcon/StreamLineIcon";
import { newDateInOsloTimeZone } from "../../../Booking/Helpers/timeZoneHelpers";
import { InsuranceServiceDto } from "@apiModels/Insurance/InsuranceServiceDto.csharp";
import { InsuranceDoctorDto } from "@apiModels/Insurance/InsuranceDoctorDto.csharp";

interface BookingFormProps {
  locations: LocationDto[];
  services: InsuranceServiceDto[];
  specializations: SpecializationDto[];
  doctor?: InsuranceDoctorDto;
  isAuthenticated?: boolean;
  loginUrl?: string;
}

export const BookingForm: FC<BookingFormProps> = (props) => {
  const {
    locations,
    specializations,
    services,
    doctor,
    isAuthenticated = true,
    loginUrl,
  } = props;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [formSubmissionStatus, setFormSubmissionStatus] =
    useState<InsuranceFormSubmissionStatus>("initial");

  const { state, dispatch } = useInsuranceForm();
  const {
    setSpecialization,
    setService,
    setDoctor,
    specialization,
    service,
    locations: selectedLocations,
  } = useInsuranceFilters();

  const { inputs, buttons } = useInsuranceLabels();

  const urlQueryParams = [
    {
      key: "specializationId",
      list: specializations,
      setter: setSpecialization,
    },
    { key: "serviceId", list: services, setter: setService },
  ];

  useEffect(() => {
    urlQueryParams.forEach((entry) => {
      const { key, list, setter } = entry;
      const foundItem = UrlHandler.getEntryFromUrlQuery<any>(
        list,
        key as UrlQueryEntryName
      );
      foundItem && setter(foundItem);
    });

    if (doctor) {
      setDoctor(doctor);
    }
  }, []);

  useEffect(() => {
    dispatch({
      field: "filters",
      type: "update",
      payload: {
        doctor,
        specialization,
        service,
        locations: selectedLocations,
      },
    });
  }, [doctor, specialization, service, selectedLocations]);

  const handleDateChange = (date: Date) => {
    const currentDate = state.date ?? newDateInOsloTimeZone();

    const newDate = set(currentDate, {
      year: getYear(date),
      month: getMonth(date),
      date: getDate(date),
    });

    dispatchValue<Date>("date", newDate);
    return;
  };

  const handleTimeChange = (time: string) => {
    const currentDate = state.date ?? newDateInOsloTimeZone();

    const [hours, minutes] = time.split(":");
    const newTimeInCurrentDate = set(currentDate, {
      hours: +hours,
      minutes: +minutes,
    });

    dispatchValue<Date>("date", newTimeInCurrentDate);
    return;
  };

  const dispatchValue = <T extends {}>(
    field: InsuranceFormFields,
    value: T | null
  ) =>
    dispatch({
      field,
      type: "update",
      payload: value,
    });

  const RequiredLabel = () => (
    <span className="insuranceBookingForm__required">
      {inputs.requiredLabel}
    </span>
  );

  const submitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    isAuthenticated && setIsModalVisible(true);
  };

  const submitData = async () => {
    if (formSubmissionStatus === "loading") return;

    setFormSubmissionStatus("loading");

    const response = await insuranceFormSubmit(state);

    if (response.isSuccess) {
      setFormSubmissionStatus("success");
      return;
    }

    setFormSubmissionStatus("error");
    return;
  };

  const handleModalClose = () => {
    if (formSubmissionStatus != "success") {
      setIsModalVisible(false);
    } else {
      window.location.href = "/";
    }
  };

  const onSubmitClick = () => {
    const urlWithSearchParams = UrlHandler.buildUrlWithQueryParameters(
      loginUrl ? loginUrl : "/login",
      [
        {
          name: "ReturnUrl",
          value: window.location.pathname + window.location.search,
        },
      ]
    );

    !isAuthenticated
      ? (window.location.href = urlWithSearchParams)
      : dispatch({
          field: "submitted",
          type: "update",
          payload: true,
        });
  };

  return (
    <div className="insuranceBookingForm">
      <form
        onSubmit={submitForm}
        className="insuranceBookingForm__form"
        encType="multipart/form-data"
        name="InsuranceBookingForm"
      >
        <div className="insuranceBookingForm__formInputs">
          <SpecializationsCombobox
            specializations={specializations}
            variant="blue-light"
          />
          <ServicesCombobox
            services={services}
            variant="blue-light"
            displayPracticalInformation
          />
          <LocationsCombobox locations={locations} variant="blue-light" />
          <div className="insuranceBookingForm__dateInputs">
            <BookingCalendar
              onChange={handleDateChange}
              disabled={!isAuthenticated}
            />
            <TimeSelect
              onChange={handleTimeChange}
              disabled={!isAuthenticated}
            />
          </div>
          <FileInput
            name="reference"
            label={inputs.referenceLabel}
            onValueChange={(value) =>
              dispatchValue<File[]>("referenceFiles", value as File[])
            }
            placeholder={inputs.referencePlaceholder}
            backgroundVariant="blue-light"
            requiredText={<RequiredLabel />}
            postfixIcon={<StreamLineIcon iconName="s-upload" />}
            error={
              state.submitted && state.referenceFiles.length === 0
                ? inputs.requiredLabel
                : undefined
            }
            multiple
            required
            disabled={!isAuthenticated}
          />
          <Input<string>
            name="caseNumber"
            label={inputs.caseNumberLabel}
            type="number"
            onValueChange={(value) =>
              dispatchValue<string>("caseNumber", value)
            }
            placeholder={inputs.caseNumberPlaceholder}
            backgroundVariant="blue-light"
            disabled={!isAuthenticated}
          />
          <Input<string>
            name="ssnNumber"
            label={inputs.ssnLabel}
            type="number"
            onValueChange={(value) => dispatchValue<string>("ssn", value)}
            placeholder={inputs.ssnPlaceholder}
            backgroundVariant="blue-light"
            disabled={!isAuthenticated}
          />
          <Textarea
            name="notes"
            label={inputs.notesLabel}
            onValueChange={(value) => dispatchValue<string>("notes", value)}
            placeholder={inputs.notesPlaceholder}
            backgroundVariant="blue-light"
            disabled={!isAuthenticated}
          />
        </div>
        <FormControls
          onSubmit={onSubmitClick}
          discardButtonLabel={buttons.discardButtonLabel}
          submitButtonLabel={
            isAuthenticated
              ? buttons.requestBookingButtonLabel
              : buttons.notLoggedInRequestBookingButtonLabel
          }
          submitButtonIcon="s-arrow-right-dash"
        />
      </form>
      <BookingConfirmationModal
        isOpen={isModalVisible}
        setIsOpen={setIsModalVisible}
        onClose={handleModalClose}
        onSubmit={submitData}
        formState={state}
        submissionStatus={formSubmissionStatus}
      />
    </div>
  );
};
